import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import animate from '@play-co/timestep-core/lib/animate';
import { assertNever } from 'src/replicant/utils';
import ImageView from '@play-co/timestep-core/lib/ui/ImageView';
import { Feature } from '../../../replicant/analytics';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import sounds from '../../../lib/sounds';
import { LangBitmapOpts } from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  trackNativeDialogButton,
  trackNativeDialogView,
} from '../../../lib/analytics/events';
import { ImageScaleViewOpts } from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { ImageScaleView } from '@play-co/timestep-core/ui';

type AppPlatform = 'ios' | 'android';

namespace style {
  export const popup = {
    height: 824,
  };

  export const icon = {
    image: 'assets/native/generic_icon.png',
    centerOnOrigin: true,
    y: 234,
    width: 429,
    height: 403,
  };

  export const gem = {
    image: 'assets/minigame/match3/gem/gameboard_gem_tile.png',
    width: 110,
    height: 91,
    centerAnchor: true,
  };
  export const gem1: ImageScaleViewOpts = {
    ...gem,
    x: 89,
    y: 195,
    scale: 0.5,
    r: -0.5,
  };
  export const gem2: ImageScaleViewOpts = {
    ...gem,
    x: 408,
    y: 134,
    scale: 0.7,
    r: 0.3,
  };
  export const gem3: ImageScaleViewOpts = {
    ...gem,
    x: 384,
    y: 233,
    scale: 0.4,
    r: -0.5,
  };

  export const message: LangBitmapOpts = {
    x: 32,
    y: 551,
    height: 130,
    width: 528,
    size: 29,
  };

  export const link = (
    buttonImg: string,
  ): Omit<ButtonScaleViewOpts, 'tracking'> => ({
    width: 507,
    height: 160,
    centerOnOrigin: true,
    y: 459,
    scaleMethod: 'contain',
    image: buttonImg,
    imagePressed: buttonImg,
    imageDisabled: buttonImg,
    pressedOffsetY: 5,
  });

  export const ok: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.secondary,
    labelOffsetY: 0,
    labelOffsetX: -9,
    fontSize: 50,
    font: 'Body',
    y: 742,
    width: 228,
    height: 94.7,
    centerOnOrigin: true,
  };
}

const getPopupSubFeature = (platform: AppPlatform) =>
  platform === 'ios'
    ? Feature.NATIVE.POPUP_INSTALL_IOS
    : platform === 'android'
    ? Feature.NATIVE.POPUP_INSTALL_ANDROID
    : assertNever(platform);

const getDownloadSubFeature = (platform: AppPlatform) =>
  platform === 'ios'
    ? Feature.NATIVE.DOWNLOAD_IOS
    : platform === 'android'
    ? Feature.NATIVE.DOWNLOAD_ANDROID
    : assertNever(platform);

export default abstract class BasePopupInstallApp extends PopupBasicLazy {
  private platform: AppPlatform;

  protected constructor(opts: {
    platform: AppPlatform;
    superview: View;
    close: () => void;
  }) {
    super({
      ...style.popup,
      ...opts,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: getPopupSubFeature(opts.platform),
      },
    });
    this.platform = opts.platform;

    this.message.updateOpts(style.message);

    this.buttonClose.onClick = async () => {
      sounds.sfx.play('cancel');
      return opts.close();
    };

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.ok,
        x: this.box.style.width / 2,
        localeText: () => i18n('basic.ok'),
        onClick: async () => this.openStore(),
        tracking: {
          feature: Feature.NATIVE._,
          $subFeature: getDownloadSubFeature(opts.platform),
        },
      }),
    );

    // game icon
    this.box.addSubview(
      new ImageView({
        ...style.icon,
        x: this.box.style.width * 0.5,
      }),
    );
    this.box.addSubview(new ImageScaleView(style.gem1));
    this.box.addSubview(new ImageScaleView(style.gem2));
    this.box.addSubview(new ImageScaleView(style.gem3));

    const buttonImg = this.getDownloadBadge();
    const linkButton = this.box.addSubview(
      new ButtonScaleView({
        x: this.box.style.width * 0.5,
        ...style.link(buttonImg),
        onClick: async () => this.openStore(),
        onDown: () => {
          animate(linkButton).commit();
          animate(linkButton).then({ scale: 0.97 }, 70, animate.easeOutBounce);
        },
        onUp: () => {
          animate(linkButton).commit();
          animate(linkButton).then({ scale: 1 }, 70 * 3, animate.easeOutBounce);
        },
        tracking: {
          feature: Feature.NATIVE._,
          $subFeature: getDownloadSubFeature(opts.platform),
        },
      }),
    );
  }

  protected getAssetsGroup(): AssetGroup {
    return new AssetGroup([
      style.icon.image,
      style.gem.image,
      this.getDownloadBadge(),
    ]);
  }

  protected abstract getDownloadBadge(): string;

  protected abstract getStoreUrl(): string;

  init(opts: {}) {
    super.init({
      title: i18n('native.install.popup.title'),
      message: i18n('native.install.popup.message'),
    });
    trackNativeDialogView(this.platform);
  }

  private openStore() {
    sounds.sfx.play('ok');
    trackNativeDialogButton(this.platform);
    window.open(this.getStoreUrl());
  }
}
